import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Avatar, 
  Button, 
  TextField, 
  Alert, 
  Snackbar,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Stack,
  LinearProgress,
  Fade,
  Zoom,
  Tooltip
} from '@mui/material';
import { 
  Edit, 
  Save, 
  AccountCircle, 
  Email, 
  VpnKey, 
  Cancel,
  TrendingUp,
  Security,
  Badge,
  Verified,
  Star,
  EmojiEvents,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import authService from '../services/authService';
import pricingService from '../services/pricingService';
import tradeService from '../services/tradeService';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        setUser(currentUser);
        
        const profileResponse = await authService.getProfile();
        setUser(profileResponse.user);
        
        setFormData({
          firstName: profileResponse.user.first_name,
          lastName: profileResponse.user.last_name,
          email: profileResponse.user.email,
          password: '',
          confirmPassword: ''
        });
        
        const plansResponse = await pricingService.getPricingPlans();
        setPlans(plansResponse);
        
        try {
          const metricsResponse = await tradeService.getPerformanceMetrics('user');
          setMetrics(metricsResponse.metrics);
        } catch (error) {
          console.error('Could not load metrics', error);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading profile data', error);
        setError('Failed to load profile data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);
  
  const handleEditToggle = () => {
    setEditMode(!editMode);
    setError('');
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      const updateData = {
        first_name: formData.firstName,
        last_name: formData.lastName
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      const response = await authService.updateProfile(updateData);
      setUser(response.user);
      
      authService.setCurrentUser(response.user);
      
      setSuccessMessage('Profile updated successfully');
      setSnackbarOpen(true);
      setEditMode(false);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Error updating profile', error);
      setError('Failed to update profile. Please try again.');
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const getCurrentPlan = () => {
    return plans.find(plan => plan.slug === user?.subscription_plan) || null;
  };
  
  const handleUpgrade = () => {
    navigate('/subscription');
  };

  const getSubscriptionStatus = () => {
    if (user?.subscription_plan === 'standard') {
      return { color: 'success', label: 'Premium Active', icon: <Verified /> };
    }
    return { color: 'default', label: 'Free Plan', icon: <Badge /> };
  };

  const getMembershipDuration = () => {
    if (!user?.created_at) return 'New Member';
    const created = new Date(user.created_at);
    const now = new Date();
    const diffInDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 30) return `${diffInDays} days`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months`;
    return `${Math.floor(diffInDays / 365)} years`;
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading your profile...</Typography>
      </Box>
    );
  }
  
  const currentPlan = getCurrentPlan();
  const subscriptionStatus = getSubscriptionStatus();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Fade in timeout={600}>
        <Paper 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)',
            borderRadius: 3,
            mb: 4,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, right: 0, opacity: 0.1 }}>
            <TrendingUp sx={{ fontSize: 200 }} />
          </Box>
          
          <Box sx={{ p: 4, position: 'relative', zIndex: 2 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item>
                <Zoom in timeout={800}>
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120,
                      background: 'linear-gradient(45deg, #cf6f61 30%, #c79a4b 90%)',
                      fontSize: '2.5rem',
                      fontWeight: 'bold',
                      border: '4px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                    }}
                  >
                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                  </Avatar>
                </Zoom>
              </Grid>
              
              <Grid item xs>
                <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Chip
                    icon={subscriptionStatus.icon}
                    label={subscriptionStatus.label}
                    color={subscriptionStatus.color}
                    sx={{ 
                      fontWeight: 600,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: subscriptionStatus.color === 'success' ? 'success.main' : 'text.primary'
                    }}
                  />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Member for {getMembershipDuration()}
                  </Typography>
                </Stack>
                
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem' }}>
                  {user?.email}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={4}>
        {/* Left Column - Stats & Subscription */}
        <Grid item xs={12} lg={4}>
          {/* Subscription Details Card - MOVED TO TOP */}
          <Fade in timeout={800}>
            <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                background: user?.subscription_plan === 'standard' 
                  ? 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)'
                  : 'linear-gradient(135deg, #3a3f45 0%, #2a2e33 100%)',
                p: 3,
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Subscription Details
                  </Typography>
                </Box>
              </Box>
              
              <CardContent>
                {currentPlan ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Plan
                      </Typography>
                      <Typography variant="h5" fontWeight={700} gutterBottom>
                        {currentPlan.name}
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        {currentPlan.price} {currentPlan.period}
                      </Typography>
                    </Box>
                    
                    {user?.subscription_plan !== 'free' && (
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Days Remaining
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {user?.subscription_time_remaining} days
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Billing Cycle
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {user?.billing_cycle?.charAt(0)?.toUpperCase() + user?.billing_cycle?.slice(1)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Next Payment
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {user?.subscription_expires_at ? 
                              new Date(user.subscription_expires_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                    
                    {currentPlan.name === 'Free' ? (
                      <Button 
                        variant="contained" 
                        fullWidth
                        onClick={handleUpgrade}
                        sx={{ 
                          background: 'linear-gradient(45deg, #cf6f61 30%, #c79a4b 90%)',
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600
                        }}
                      >
                        Upgrade to Premium
                      </Button>
                    ) : (
                      <Button 
                        variant="outlined" 
                        fullWidth
                        onClick={handleUpgrade}
                        sx={{ borderRadius: 2, py: 1.5 }}
                      >
                        Manage Subscription
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Plan information not available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Fade>

          {/* Trading Performance Card - MOVED TO SECOND POSITION */}
          {metrics && metrics.totalTrades > 0 && (
            <Fade in timeout={1000}>
              <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #46a07a 0%, #86c9a6 100%)',
                  p: 3,
                  color: 'white'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmojiEvents sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={700}>
                      Trading Performance
                    </Typography>
                  </Box>
                </Box>
                
                <CardContent sx={{ pt: 3 }}>
                  {/* Leverage Warning */}
                  <Alert 
                    severity="warning" 
                    icon={<InfoIcon />}
                    sx={{ mb: 3, fontSize: '0.75rem' }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      ⚠️ <strong>Important:</strong> Multiply by broker's leverage for real results.
                    </Typography>
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight={700}>
                          {metrics.winRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Win Rate
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="h4" 
                          fontWeight={700}
                          color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {parseFloat(metrics.totalProfitPercentage) >= 0 ? '+' : ''}{metrics.totalProfitPercentage}%
                          <Tooltip title="⚠️ Multiply by broker's leverage for real returns">
                            <InfoIcon sx={{ fontSize: 14, ml: 0.5, color: 'warning.main' }} />
                          </Tooltip>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Return
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Leverage Calculations */}
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          With leverage:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <Chip 
                            label={`1:20 = ${(parseFloat(metrics.totalProfitPercentage) * 20).toFixed(1)}%`}
                            size="small" 
                            variant="outlined"
                            color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                            sx={{ fontSize: '0.6rem', height: '20px' }}
                          />
                          <Chip 
                            label={`1:30 = ${(parseFloat(metrics.totalProfitPercentage) * 30).toFixed(1)}%`}
                            size="small" 
                            variant="outlined"
                            color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                            sx={{ fontSize: '0.6rem', height: '20px' }}
                          />
                          <Chip 
                            label={`1:500 = ${(parseFloat(metrics.totalProfitPercentage) * 500).toFixed(1)}%`}
                            size="small" 
                            variant="outlined"
                            color={parseFloat(metrics.totalProfitPercentage) >= 0 ? 'success' : 'error'}
                            sx={{ fontSize: '0.6rem', height: '20px' }}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* Total Pips */}
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="h6" 
                          fontWeight={700}
                          color={parseFloat(metrics.totalPips) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {parseFloat(metrics.totalPips) >= 0 ? '+' : ''}{metrics.totalPips}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Pips
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Total Trades */}
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {metrics.totalTrades}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Trades
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Successful Trades</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {metrics.winningTrades}/{metrics.totalTrades}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(metrics.winningTrades / metrics.totalTrades) * 100} 
                        sx={{ height: 8, borderRadius: 4 }}
                        color="success"
                      />
                    </Grid>

                    {/* View Full Dashboard Button */}
                    <Grid item xs={12}>
                      <Button 
                        variant="outlined" 
                        fullWidth
                        onClick={() => navigate('/dashboard')}
                        startIcon={<TrendingUp />}
                        sx={{ borderRadius: 2, mt: 1 }}
                      >
                        View Full Dashboard
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Fade>
          )}
          
          {/* No Trading Performance Alert */}
          {metrics && metrics.totalTrades === 0 && (
            <Fade in timeout={1000}>
              <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #3a3f45 0%, #2a2e33 100%)',
                  p: 3,
                  color: 'white'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmojiEvents sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={700}>
                      Trading Performance
                    </Typography>
                  </Box>
                </Box>
                
                <CardContent sx={{ pt: 3 }}>
                  <Alert severity="info">
                    <Typography variant="body2" gutterBottom>
                      You don't have any trade history yet.
                    </Typography>
                    {user?.subscription_plan === 'free' ? (
                      <Typography variant="caption">
                        Upgrade to premium for trading signals!
                      </Typography>
                    ) : (
                      <Typography variant="caption">
                        Start trading to see your performance.
                      </Typography>
                    )}
                  </Alert>
                </CardContent>
              </Card>
            </Fade>
          )}
        </Grid>
        
        {/* Right Column - Profile Information - UNCHANGED */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={1200}>
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {/* Header - COLORFUL GRADIENT BANNER */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 3,
                background: 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)',
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountCircle sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={700}>
                    Account Information
                  </Typography>
                </Box>
                
                <Button 
                  variant={editMode ? "contained" : "outlined"}
                  startIcon={editMode ? <Save /> : <Edit />}
                  onClick={editMode ? handleSubmit : handleEditToggle}
                  sx={{ 
                    borderRadius: 2,
                    ...(editMode ? {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)'
                      }
                    } : {
                      borderColor: 'rgba(255,255,255,0.7)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    })
                  }}
                >
                  {editMode ? 'Save Changes' : 'Edit Profile'}
                </Button>
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="First Name"
                        name="firstName"
                        fullWidth
                        value={formData.firstName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                        InputProps={{
                          startAdornment: <AccountCircle sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Name"
                        name="lastName"
                        fullWidth
                        value={formData.lastName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                        InputProps={{
                          startAdornment: <AccountCircle sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label="Email Address"
                        name="email"
                        fullWidth
                        value={formData.email}
                        disabled={true}
                        variant="outlined"
                        helperText="Email cannot be changed for security reasons"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                        InputProps={{
                          startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                    
                    {editMode && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="New Password"
                            name="password"
                            type="password"
                            fullWidth
                            value={formData.password}
                            onChange={handleInputChange}
                            variant="outlined"
                            helperText="Leave blank to keep current password"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }}
                            InputProps={{
                              startAdornment: <VpnKey sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Confirm New Password"
                            name="confirmPassword"
                            type="password"
                            fullWidth
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            variant="outlined"
                            helperText="Leave blank to keep current password"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }}
                            InputProps={{
                              startAdornment: <VpnKey sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button 
                              variant="outlined" 
                              startIcon={<Cancel />}
                              onClick={handleEditToggle}
                              sx={{ borderRadius: 2 }}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="contained" 
                              type="submit"
                              startIcon={<Save />}
                              sx={{ borderRadius: 2 }}
                            >
                              Save Changes
                            </Button>
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Fade>
          
          {/* Account Security Section - UNCHANGED */}
          <Fade in timeout={1400}>
            <Card sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                p: 3,
                background: 'linear-gradient(135deg, #5980a6 0%, #cf6f61 100%)',
                color: 'white'
              }}>
                <Security sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" fontWeight={700}>
                  Account Security
                </Typography>
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Login
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {user?.last_login ? 
                          new Date(user.last_login).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Account Created
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {new Date(user?.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        Your account is secured with industry-standard encryption. 
                        For additional security, consider enabling two-factor authentication.
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
      
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="success" 
          sx={{ width: '100%', borderRadius: 2 }}
          action={
            <IconButton size="small" onClick={handleSnackbarClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;